import type {
  InfiniteLeaderboard,
  LeaderboardEntry,
  LeaderboardParams,
  Period,
  SLeaderboardEntry,
} from '~/types';
import {
  createInfiniteQuery,
  createMutation,
  useQueryClient,
} from '@tanstack/svelte-query';
import { localLeaderboard } from '~/leaderboard/leaderboard.svelte';
import { getLeaderboard, submitScore } from '~/lib/leaderboard.remote.js';
import { freshQuery } from '~/utils/freshQuery';

export const loadLocalLeaderboard = (): LeaderboardEntry[] => {
  return localLeaderboard.current
    .filter((g) => g.score)
    .map((g) => ({
      ...g,
      id: g.gameId,
      userName: g.userName ?? (g as any).name,
      numTurns: g.numTurns ?? (g as any).turns,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 50);
};

export const getLeaderboardQueryKey = (period: Period, gameId: string) => [
  'leaderboard',
  period,
  gameId,
];

const pageIsCurrentGame = (page: SLeaderboardEntry[], id: string) =>
  page.length === 1 && page[0].id === id;

export type LeaderboardPages = { pages: SLeaderboardEntry[] };

export const loadLeaderboardQuery = (period: Period, gameId: string) =>
  createInfiniteQuery<
    SLeaderboardEntry[],
    unknown,
    LeaderboardPages,
    string[],
    LeaderboardParams
  >(() => ({
    queryKey: getLeaderboardQueryKey(period, gameId),
    queryFn: async (ctx) => {
      if (period === 'local') {
        return loadLocalLeaderboard() as unknown as SLeaderboardEntry[];
      }
      const { rank: pageRank, ...pageParams } = ctx.pageParam;
      let rows = await freshQuery(getLeaderboard({ ...pageParams, period }));
      let rank = pageRank;
      if (ctx.pageParam?.direction === 'asc') {
        return rows.map((r) => ({ ...r, rank: --rank })).reverse();
      } else {
        return rows.map((r) => ({ ...r, rank: ++rank }));
      }
    },
    initialPageParam: {
      direction: 'desc',
      rank: 0,
      period,
    },
    getNextPageParam: (lastPage, pages): LeaderboardParams | undefined => {
      if (
        pages.length &&
        (!lastPage.length || lastPage.length < 10) &&
        !pageIsCurrentGame(lastPage, gameId)
      ) {
        return undefined;
      }
      return {
        period,
        id: lastPage[lastPage.length - 1].id,
        score: lastPage[lastPage.length - 1].score.toString(),
        rank: lastPage[lastPage.length - 1].rank,
        direction: 'desc',
      };
    },
    getPreviousPageParam: (firstPage, pages): LeaderboardParams | undefined => {
      if (
        pages.length &&
        firstPage.length < 10 &&
        !pageIsCurrentGame(firstPage, gameId)
      ) {
        return undefined;
      }
      return {
        period,
        id: firstPage[0].id,
        score: firstPage[0].score.toString(),
        rank: firstPage[0].rank,
        direction: 'asc',
      };
    },
    enabled: Boolean(period),
  }));

export const submitScoreMutation = () => {
  const queryClient = useQueryClient();
  return createMutation<SLeaderboardEntry, Error, SLeaderboardEntry>(() => ({
    mutationFn: async (game: SLeaderboardEntry) => {
      const { name, turns, rank, ...post } = game;
      return await submitScore(post);
    },
    onSuccess: (game: SLeaderboardEntry) => {
      (['week', 'all'] as Period[]).forEach((tab) => {
        queryClient.setQueryData<InfiniteLeaderboard>(
          getLeaderboardQueryKey(tab, game.gameId),
          {
            pageParams: [
              {
                id: game.gameId,
                direction: 'desc',
                score: game.score.toString(),
                rank: game[`${tab}_rank`],
                period: tab,
              },
            ],
            pages: [[{ ...game, rank: game[`${tab}_rank`] }]],
          },
        );
      });
    },
  }));
};
